import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, renderSlot as _renderSlot, normalizeClass as _normalizeClass, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-e513b1c0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "bg-[#F0F0F4] rounded-xl p-4 w-full relative" }
const _hoisted_2 = {
  key: 0,
  class: "overflow-x-auto sheet-scrollbar pb-3"
}
const _hoisted_3 = { class: "w-full min-w-max border-collapse" }
const _hoisted_4 = { class: "text-[#1F2937] text-sm" }
const _hoisted_5 = { class: "flex items-center justify-center gap-1.5" }
const _hoisted_6 = {
  viewBox: "0 0 24 24",
  width: "16",
  height: "16",
  class: "text-current"
}
const _hoisted_7 = ["d"]
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = { key: 1 }
const _hoisted_10 = { class: "uppercase" }
const _hoisted_11 = {
  key: 0,
  class: "flex items-center justify-start gap-2 min-w-[100px]"
}
const _hoisted_12 = {
  key: 0,
  viewBox: "0 0 24 24",
  class: "w-5 h-5 text-green-600 flex-shrink-0"
}
const _hoisted_13 = ["d"]
const _hoisted_14 = {
  key: 1,
  viewBox: "0 0 24 24",
  class: "w-5 h-5 text-orange-500 flex-shrink-0"
}
const _hoisted_15 = ["d"]
const _hoisted_16 = {
  key: 2,
  class: "text-gray-800"
}
const _hoisted_17 = {
  key: 1,
  class: "overflow-x-auto sheet-scrollbar pb-3"
}
const _hoisted_18 = { class: "w-full min-w-max border-collapse" }
const _hoisted_19 = { class: "text-[#1F2937] text-sm" }
const _hoisted_20 = { class: "flex items-center justify-center gap-1.5" }
const _hoisted_21 = {
  viewBox: "0 0 24 24",
  width: "16",
  height: "16",
  class: "text-current"
}
const _hoisted_22 = ["d"]
const _hoisted_23 = ["innerHTML"]
const _hoisted_24 = { key: 1 }
const _hoisted_25 = { class: "uppercase" }
const _hoisted_26 = ["type", "value", "data-testid", "aria-invalid", "title", "onInput"]
const _hoisted_27 = ["title"]
const _hoisted_28 = {
  key: 2,
  class: "text-gray-800 py-2 inline-block"
}
const _hoisted_29 = {
  key: 2,
  class: "attendance-spreadsheet",
  "data-testid": "attendance-spreadsheet"
}

import { ref, computed, watch, onMounted, onBeforeUnmount, defineComponent, h, PropType } from 'vue'
import jspreadsheet from 'jspreadsheet-ce'
import type { CellChange } from 'jspreadsheet-ce'
import 'jspreadsheet-ce/dist/jspreadsheet.css'
import jSuites from 'jsuites'
import 'jsuites/dist/jsuites.css'
import { useToast } from 'vue-toastification'
import {
  mdiCheckCircleOutline,
  mdiAlertCircleOutline,
  mdiInformationOutline,
  mdiMenuDown
} from '@mdi/js'
import { toCanonicalDateOnly } from '@/utils/date'
import type {
  SheetColumn,
  SheetColumnCustomEditor,
  SheetColumnMenuItem,
  SheetWorksheetInstance
} from './attendanceSheet.types'
import { canInsertRows, canDeleteRows } from './sheetRowLimits'
import { toCssWidth, toGridWidth } from './sheetColumnWidths'
import { computePasteBlock, buildPasteSummaryMessage } from './sheet-paste'
import { createSheetTimeCellEditor } from './sheetTimeCellEditor'
import { isSelectRowShortcut } from './sheet-keyboard'
import {
  markSheetGridActive,
  isLastActiveSheetGrid,
  clearIfLastActiveSheetGrid
} from './sheetGridActiveHolder'

/**
 * B4/UAT-51 — títulos pt-BR dos dois itens de linha que `handleContextMenu` também
 * precisa reconhecer para desabilitá-los no teto/piso (RN-09/RN-11). Constantes
 * compartilhadas com o dicionário abaixo: depois da tradução, o `item.title` que a lib
 * repassa ao `contextMenu` já vem em pt-BR — comparar contra o literal em inglês nunca
 * mais casaria, silenciando o bloqueio.
 */
const ROW_INSERT_BEFORE_TITLE = 'Inserir linha acima'
const ROW_INSERT_AFTER_TITLE = 'Inserir linha abaixo'
const ROW_DELETE_TITLE = 'Excluir linha selecionada'

/**
 * B4/UAT-51 — o menu de contexto nativo do jspreadsheet CE (via `jSuites.translate`) vem
 * em inglês por padrão. `setDictionary` é global ao módulo `jsuites`, então é chamado uma
 * única vez no carregamento deste arquivo, não a cada montagem do grid.
 */
type SheetRow = Record<string, unknown>
type EditableSheetRow = Record<string, string | boolean>
type JspreadsheetInstanceElement = Parameters<typeof jspreadsheet.destroy>[0]
type InsertedRow = { row: number; data: (string | number | boolean)[] }

/**
 * Subconjunto do `HistoryRecord` da lib usado só para reconhecer, no `onredo`, que o
 * passo refeito foi uma inserção de linha (ver `reprefillRedoneRows`).
 */
type InsertRowHistoryRecord = {
  action: 'insertRow'
  rowNumber: number
  numOfRows: number
  insertBefore: boolean
}

/**
 * Subconjunto de `ContextMenuItem` da lib usado só para desabilitar, com motivo, os
 * itens de inserção/exclusão de linha no teto/piso (RN-09/RF-12/RN-11 — ver
 * `handleContextMenu`).
 */
type ContextMenuItem = {
  title: string
  disabled?: boolean
  tooltip?: string
  [key: string]: unknown
}

const CELL_ERROR_MESSAGE = 'Campo obrigatório: a linha está em uso e precisa ficar completa.'
const DATA_TESTID_ATTR = 'data-testid'


export default /*#__PURE__*/_defineComponent({
  __name: 'AttendanceSheetCard',
  props: {
  columns: {
    type: Array as PropType<SheetColumn[]>,
    required: true,
    default: () => []
  },
  rows: {
    type: Array as PropType<SheetRow[]>,
    required: true,
    default: () => []
  },
  mode: {
    type: String as PropType<'view' | 'edit'>,
    default: 'view',
    validator: (value: string) => ['view', 'edit'].includes(value)
  },
  /**
   * PRD 32/#2114 — qual grade o modo `edit` renderiza. `native` é a tabela editável de
   * produção (slots `cell-edit-*`), o caminho de flag desligada; `spreadsheet` é o
   * jspreadsheet CE, o caminho de flag ligada. Quem decide entre os dois é a tela chamadora.
   */
  engine: {
    type: String as PropType<'native' | 'spreadsheet'>,
    default: 'native',
    validator: (value: string) => ['native', 'spreadsheet'].includes(value)
  },
  /**
   * Índice da linha parcial -> chaves das colunas que faltam preencher nela. Linha presente
   * no mapa ganha a faixa de linha parcial; suas colunas listadas, erro de validação na célula.
   */
  rowErrors: {
    type: Object as PropType<Record<number, string[]>>,
    default: () => ({})
  },
  /**
   * RF-01/RF-07 (#2115) — valor de nascença de cada coluna, usado só para prefiller a
   * linha que o auditor cria depois (via `allowInsertRow` do jspreadsheet). Colunas fora
   * deste mapa nascem vazias, como hoje. A tela chamadora já calcula o mesmo valor para a
   * linha inicial do modelo (`defaultRow`) — este prop é a mesma fonte.
   */
  newRowDefaults: {
    type: Object as PropType<Record<string, string | boolean>>,
    default: () => ({})
  },
  /**
   * RF-03 (#2111) — editor de célula customizado por coluna (mecanismo `CustomEditor`
   * nativo do jspreadsheet). Este componente é domínio-agnóstico: só aplica o objeto na
   * configuração da coluna correspondente, sem saber o que ele faz.
   */
  columnEditors: {
    type: Object as PropType<Record<string, SheetColumnCustomEditor>>,
    default: () => ({})
  },
  /**
   * RF-06 (#2113) — menu `▾` do cabeçalho da coluna, na engine `spreadsheet`. Domínio-
   * agnóstico: recebe a chave da coluna e devolve o item único (rótulo/estado/ação) que
   * o módulo chamador decidiu — `undefined` para colunas sem menu.
   */
  getColumnMenuItem: {
    type: Function as PropType<(columnKey: string) => SheetColumnMenuItem | undefined>,
    default: undefined
  },
  /**
   * RN-09/RF-12 (#2119) — teto e piso de linhas na engine `spreadsheet`; domínio-agnóstico
   * (o número e os motivos vêm de quem chama, `sheetRowLimits.ts` neste mesmo diretório)
   * — este componente só aplica a regra às três vias nativas do jspreadsheet
   * (botão "Adicionar linha" via `addRow()` exposto, menu de contexto, colagem).
   */
  maxRows: {
    type: Number,
    default: 100
  },
  minRows: {
    type: Number,
    default: 1
  },
  /** RN-11 — motivo exibido (tooltip do menu de contexto) quando o teto de linhas é atingido. */
  rowLimitReason: {
    type: String,
    default: 'Limite de linhas atingido.'
  },
  /** Motivo exibido quando a exclusão deixaria a folha abaixo do piso de linhas. */
  minRowLimitReason: {
    type: String,
    default: 'A folha precisa manter ao menos 1 linha.'
  }
},
  emits: ['update:rows'],
  setup(__props, { expose: __expose, emit }) {

const props = __props;

jSuites.setDictionary({
  'Insert a new row before': ROW_INSERT_BEFORE_TITLE,
  'Insert a new row after': ROW_INSERT_AFTER_TITLE,
  'Delete selected rows': ROW_DELETE_TITLE,
  'Insert a new column before': 'Inserir coluna à esquerda',
  'Insert a new column after': 'Inserir coluna à direita',
  'Delete selected columns': 'Excluir coluna selecionada',
  'Rename this column': 'Renomear esta coluna',
  'Order ascending': 'Ordenar de A a Z',
  'Order descending': 'Ordenar de Z a A',
  Copy: 'Copiar',
  Paste: 'Colar',
  'Save as': 'Salvar como',
  About: 'Sobre',
  Menu: 'Menu',
  close: 'fechar'
})

/**
 * O modo `view` também aceita células de status (`{ type, text }`) na coluna de
 * conciliação — só o modo `edit`, que alimenta o jspreadsheet, exige `string | boolean`.
 */




function asStatus(value: unknown): { type: string; text: string } | undefined {
  if (value && typeof value === 'object' && 'type' in value && 'text' in value) {
    return value as { type: string; text: string }
  }
  return undefined
}

/** Engine `native` (flag desligada) — mesma faixa de linha parcial e erro de célula do RF-02. */
function isPartialRow(rowIndex: number): boolean {
  return props.rowErrors[rowIndex] !== undefined
}

function isCellInvalid(rowIndex: number, columnKey: string): boolean {
  return props.rowErrors[rowIndex]?.includes(columnKey) ?? false
}

const localRows = ref<SheetRow[]>([])

watch(
  () => props.rows,
  (newRows) => {
    if (props.engine !== 'native') return
    if (JSON.stringify(newRows) !== JSON.stringify(localRows.value)) {
      localRows.value = cloneRows(newRows)
    }
  },
  { immediate: true, deep: true }
)

function updateEditCell(rowIndex: number, columnKey: string, value: string | boolean) {
  const row = localRows.value[rowIndex]
  if (!row) return
  row[columnKey] = value
  emit('update:rows', localRows.value)
}

const gridContainer = ref<HTMLDivElement | null>(null)
const worksheet = ref<SheetWorksheetInstance | null>(null)

/**
 * RF-07 (#2115) — o histórico é interno ao jspreadsheet (`worksheet.history`), não um
 * estado Vue; estas refs só espelham `historyIndex` a cada mutação para o botão
 * "Desfazer" da tela saber quando habilitar/desabilitar.
 */
const canUndo = ref(false)
const canRedo = ref(false)

/**
 * RN-09/RF-12 — quantidade de linhas da GRADE, não de `props.rows`: sem modelo de folha
 * selecionado a tela mantém `rows: []` enquanto a lib materializa 1 linha por
 * `minDimensions` (ver `createGrid`), e o gate do botão "Excluir última linha" precisa
 * enxergar o que está na tela. Espelhada a cada mutação pelo `syncFromGrid`, do mesmo
 * jeito que `canUndo`/`canRedo` espelham o histórico interno da lib.
 */
const gridRowCount = ref(0)

function refreshRowCount(instance: SheetWorksheetInstance | null) {
  // `instance.rows` é a mesma fonte já usada pelos outros gates de linha
  // (`handleBeforeInsertRow`/`handleBeforeDeleteRow`/`handleContextMenu`) — uma contagem só
  // para o mesmo conceito.
  gridRowCount.value = instance?.rows.length ?? 0
}

/** RN-09 — piso de linhas: com a folha no mínimo, não há o que excluir. */
const canDeleteRow = computed(() => canDeleteRows(gridRowCount.value, 1, props.minRows))

function refreshHistoryState() {
  const instance = worksheet.value
  if (!instance) {
    canUndo.value = false
    canRedo.value = false
    return
  }
  canUndo.value = instance.historyIndex >= 0
  canRedo.value = instance.historyIndex < instance.history.length - 1
}

function undo() {
  worksheet.value?.undo()
}

function redo() {
  worksheet.value?.redo()
}

/**
 * UAT-43 — os atalhos nativos Ctrl+Z/Ctrl+Y da lib operam sobre `jspreadsheet.current`
 * (fonte, `keyDownControls`: `if (libraryBase.jspreadsheet.current) { ... }`), que a
 * própria lib zera em qualquer mousedown fora da grade — depois disso os atalhos nativos
 * simplesmente não fazem nada, mesmo com esta grade sendo a única em edição na tela.
 * Handler próprio registrado em `document` na criação da grade (síncrono, antes do
 * listener assíncrono da lib — mesma técnica de `attachFillHandleGuard`): ordem de
 * registro em `document` decide ordem de execução, então o nosso roda primeiro e chama
 * `stopImmediatePropagation` para o listener nativo nunca rodar sobre o mesmo evento —
 * sem isso, com `jspreadsheet.current` setado, os dois processariam o mesmo Ctrl+Z e
 * desfariam dois passos de histórico de uma vez.
 *
 * Ciclo 1 de review (PR web#2337) — sem escopo, o handler engolia Ctrl+Z de QUALQUER
 * componente da página (o handler nem checava se o evento tinha algo a ver com esta
 * grade) e, com duas grades montadas, a primeira a registrar o listener sempre vencia a
 * segunda via `stopImmediatePropagation`. Duas condições, qualquer uma libera o
 * tratamento; nenhuma delas, devolve sem tocar no evento (nem `preventDefault`, para não
 * engolir o atalho de quem quer que seja o dono real):
 *   1. `event.target` está dentro do container desta grade — a interação claramente é
 *      com esta grade (célula, editor, cabeçalho etc).
 *   2. `event.target` é "neutro" (`document`/`document.body` — nenhum elemento
 *      específico está com foco, o caso de UAT-43: mousedown fora da grade só para
 *      desfocar a célula, sem focar outro widget) E esta foi a última grade
 *      interagida (`isLastActiveSheetGrid`, atualizado em `markSheetGridActive` — ver
 *      mousedown/focusin no container e `syncFromGrid`). Isso distingue "cliquei fora só
 *      para desfocar" (ainda deve funcionar) de "cliquei/focei outro componente
 *      específico da página" (não deve ser engolido).
 */
/** Ver comentário de `handleShortcutKeyDown` acima — as duas condições de escopo. */
function shortcutBelongsToThisGrid(container: HTMLElement, target: Node | null): boolean {
  const targetInsideGrid = target !== null && container.contains(target)
  if (targetInsideGrid) return true

  const targetIsNeutral = target === document || target === document.body
  return targetIsNeutral && isLastActiveSheetGrid(container)
}

/** `undefined` para qualquer combinação que não seja o atalho de desfazer/refazer. */
function shortcutActionFor(event: KeyboardEvent): 'undo' | 'redo' | undefined {
  if (!(event.ctrlKey || event.metaKey)) return undefined

  const key = event.key.toLowerCase()
  if (key === 'z') return event.shiftKey ? 'redo' : 'undo'
  if (key === 'y') return 'redo'
  return undefined
}

/**
 * N7/#2360 — origem do evento, para o `handleBeforeChange`. O `onbeforechange` da lib não
 * recebe o evento que originou a escrita, e o Delete de teclado e o RESET do calendário
 * escrevem o MESMO `''`: o valor não distingue as duas origens, só o evento distingue.
 *
 * Armado no `keydown` em captura que já existe (escopado à grade pelas mesmas duas condições
 * dos atalhos) e desarmado no microtask seguinte — que roda depois de toda a cadeia síncrona
 * de `onbeforechange` do mesmo evento, inclusive um Delete sobre um range de células.
 * Microtask e não `setTimeout` de propósito: não depende de timer avançado em teste.
 */
let emptyWriteFromKeyboard = false

/** N7/#2360 — marca Delete/Backspace como origem da escrita, distinguindo-a do RESET do calendário. */
function handleDeleteKeyboard(event: KeyboardEvent) {
  if (event.key !== 'Delete' && event.key !== 'Backspace') return
  if (!shortcutBelongsToThisGrid(gridContainer.value!, event.target as Node | null)) return

  emptyWriteFromKeyboard = true
  queueMicrotask(() => {
    emptyWriteFromKeyboard = false
  })
}

/**
 * RF-10 (#2118) — `Shift+Espaço` seleciona a linha inteira. Único atalho novo da J-04
 * sem equivalente nativo (`keyDownControls` da lib trata Espaço igual com ou sem Shift —
 * ver `sheet-keyboard.ts`); dispara apenas FORA de edição — dentro dela `Espaço` é um
 * caractere digitável normal e não deve ser interceptado.
 */
function handleSelectRowKeyboard(event: KeyboardEvent) {
  if (!isSelectRowShortcut(event)) return
  if (worksheet.value!.edition || !worksheet.value!.selectedCell) return
  if (!shortcutBelongsToThisGrid(gridContainer.value!, event.target as Node | null)) return

  event.preventDefault()
  event.stopImmediatePropagation()
  const rowIndex = Number(worksheet.value!.selectedCell[1])
  worksheet.value!.updateSelectionFromCoords(0, rowIndex, props.columns.length - 1, rowIndex)
}

/** RF-07 (#2115) — Ctrl+Z/Ctrl+Y desfazer/refazer. */
function handleUndoRedoKeyboard(event: KeyboardEvent) {
  const action = shortcutActionFor(event)
  if (!action) return
  if (!shortcutBelongsToThisGrid(gridContainer.value!, event.target as Node | null)) return

  event.preventDefault()
  event.stopImmediatePropagation()
  if (action === 'undo') undo()
  else redo()
}

function handleShortcutKeyDown(event: KeyboardEvent) {
  if (!worksheet.value || !gridContainer.value) return

  handleDeleteKeyboard(event)
  handleSelectRowKeyboard(event)
  handleUndoRedoKeyboard(event)
}

/**
 * B5 (PRD 32 run 3) — âncora do `historyIndex` capturada em `handleBeforePaste` (o
 * único handler de `onbeforepaste` da grade — também usado pelo teto de linhas do
 * #2119, RF-12), para
 * `mergeBatchPasteHistory` reconhecer, no `onafterchanges` da mesma colagem, quantos
 * registros de histórico ela produziu.
 */
let pasteAnchorHistoryIndex: number | null = null

/**
 * RN-07 — uma colagem que cresce a tabela grava DOIS registros de histórico na lib: o
 * `insertRow` das linhas novas (disparado de dentro da própria rotina de colagem) e,
 * depois, um `setValue` com todas as células coladas — inclusive as de linhas que já
 * existiam antes da colagem (não cobertas pelo `insertRow`). Um único Ctrl+Z precisa
 * desfazer os dois; como o `undo` nativo só processa UM registro por chamada e decide o
 * que fazer olhando `action` (não dá pra inventar uma `action` composta que ele entenda),
 * a solução é marcar o registro de `setValue` como pareado com o `insertRow` anterior — e
 * `patchUndoRedoForPasteGrowth` (chamado uma vez, na criação da grade) encadeia duas
 * chamadas nativas de `undo`/`redo` sempre que o registro no topo da pilha estiver
 * marcado, para qualquer chamador (o `undo()` exposto por este componente OU o atalho de
 * teclado Ctrl+Z, que chama `instance.undo()` diretamente).
 */
function mergeBatchPasteHistory(instance: SheetWorksheetInstance) {
  if (pasteAnchorHistoryIndex === null) return
  const anchor = pasteAnchorHistoryIndex
  pasteAnchorHistoryIndex = null

  const insertedAt = anchor + 1
  const valueAt = anchor + 2
  if (instance.historyIndex !== valueAt) return

  const insertRecord = instance.history[insertedAt] as { action?: string } | undefined
  const valueRecord = instance.history[valueAt] as Record<string, unknown> | undefined
  if (insertRecord?.action !== 'insertRow' || valueRecord?.action !== 'setValue') return

  valueRecord.__pasteGrowthPair = true
}

/**
 * Encadeia `undo`/`redo` nativos para o par marcado por `mergeBatchPasteHistory`. Chamado
 * uma única vez por instância, na criação da grade — sobrescreve os métodos originais
 * (mesma técnica de extensão do jspreadsheet: reatribuir a propriedade na instância).
 */
function patchUndoRedoForPasteGrowth(instance: SheetWorksheetInstance) {
  const nativeUndo = instance.undo.bind(instance)
  const nativeRedo = instance.redo.bind(instance)

  instance.undo = () => {
    const topRecord = instance.history[instance.historyIndex] as Record<string, unknown> | undefined
    nativeUndo()
    if (topRecord?.__pasteGrowthPair) nativeUndo()
  }

  instance.redo = () => {
    nativeRedo()
    const nextRecord = instance.history[instance.historyIndex + 1] as
      | Record<string, unknown>
      | undefined
    if (nextRecord?.__pasteGrowthPair) nativeRedo()
  }
}

/**
 * UAT-46 — o jspreadsheet só grava a célula em edição no modelo interno (e dispara
 * `onafterchanges`/`syncFromGrid`) ao fechar o editor (blur/Enter/Tab); enquanto o
 * editor está aberto, `props.rows` (e portanto `hasRowsInUse`) ainda reflete o valor
 * anterior. Quem decide trocar de modelo de folha (`handleSheetModelSelection`) precisa
 * commitar essa edição pendente antes de avaliar `hasRowsInUse`, senão uma linha
 * preenchida via edição em aberto passa como vazia e pula o modal de confirmação.
 */
function commitPendingEdit() {
  const instance = worksheet.value
  const editingCell = instance?.edition?.[0]
  if (instance && editingCell) {
    instance.closeEditor(editingCell, true)
  }
}

__expose({ undo, redo, canUndo, canRedo, addRow, deleteRow, canDeleteRow, commitPendingEdit })

/**
 * Mapa de tipo de coluna do domínio -> tipo de célula do jspreadsheet: `checkbox` e `number`
 * caem nos tipos nativos equivalentes, `date` no `calendar` já formatado em `DD/MM/YYYY`,
 * `readonly`/`status` em `text` bloqueado e `text` no `text` nativo. `time` não tem
 * equivalente nativo no CE e usa o editor customizado `createSheetTimeCellEditor` — máscara
 * `##:##` na digitação e descarte da hora inválida ao fechar a célula, a mesma RN-08 que a
 * colagem aplica.
 */
function toGridColumn(col: SheetColumn): Record<string, unknown> {
  const base = {
    name: col.key,
    title: col.label,
    width: toGridWidth(col),
    wordWrap: false
  }

  const gridColumn = (() => {
    switch (col.type) {
      case 'checkbox':
        return { ...base, type: 'checkbox' as const }
      case 'date':
        return {
          ...base,
          type: 'calendar' as const,
          options: { format: 'DD/MM/YYYY', time: false, resetButton: true }
        }
      case 'number':
        return { ...base, type: 'numeric' as const }
      case 'readonly':
      case 'status':
        return { ...base, type: 'text' as const, readOnly: true }
      case 'time':
        // Uma instância por coluna (este `toGridColumn` roda uma vez por coluna), como no
        // editor de CRM/nome: o editor guarda o estado da edição em curso em closure, e um
        // singleton de módulo colocaria duas grades da mesma página disputando esse estado.
        return { ...base, type: createSheetTimeCellEditor() }
      case 'text':
      default:
        return { ...base, type: 'text' as const }
    }
  })()

  // RF-03 (#2111) — a coluna com editor injetado troca o `type` string pelo objeto
  // `CustomEditor`: jspreadsheet delega inteiramente a abertura/fechamento da edição a ele.
  const columnEditor = props.columnEditors[col.key]
  if (columnEditor) return { ...gridColumn, type: columnEditor }

  return gridColumn
}

function cloneRows(rows: SheetRow[]): EditableSheetRow[] {
  return JSON.parse(JSON.stringify(rows))
}

function handleGridContainerActivity() {
  if (gridContainer.value) markSheetGridActive(gridContainer.value)
}

function destroyGrid() {
  document.removeEventListener('keydown', handleShortcutKeyDown, true)
  if (gridContainer.value) {
    gridContainer.value.removeEventListener('mousedown', handleGridContainerActivity)
    gridContainer.value.removeEventListener('focusin', handleGridContainerActivity)
    clearIfLastActiveSheetGrid(gridContainer.value)
  }
  detachFillHandleGuard?.()
  detachFillHandleGuard = null
  if (gridContainer.value) {
    jspreadsheet.destroy(gridContainer.value as unknown as JspreadsheetInstanceElement, true)
  }
  worksheet.value = null
  refreshRowCount(null)
}

const toast = useToast()

/**
 * RN-09/RF-12 (#2119) — bloqueia inserir linhas que ultrapassariam `maxRows`. Cobre as
 * três vias por igual: botão "Adicionar linha" (via `addRow()` exposto), menu de
 * contexto e crescimento automático da tabela durante a colagem.
 */
function handleBeforeInsertRow(
  instance: SheetWorksheetInstance,
  rows: { row: number; data: unknown[] }[]
): boolean | undefined {
  return canInsertRows(instance.rows.length, rows.length, props.maxRows) ? undefined : false
}

/** RN-09 — bloqueia excluir linhas que deixariam a folha abaixo de `minRows`. */
function handleBeforeDeleteRow(
  instance: SheetWorksheetInstance,
  removedRows: number[]
): boolean | undefined {
  return canDeleteRows(instance.rows.length, removedRows.length, props.minRows) ? undefined : false
}

/**
 * RN-11 — os itens de inserção/exclusão de linha do menu de contexto nativo ficam
 * desabilitados no teto/piso, com o mesmo motivo das outras vias, em `tooltip` (o
 * próprio mecanismo de motivo à vista que a lib já suporta nesse item). A lib não expõe
 * `id` nos itens default, só `title` — identificados pelos títulos pt-BR (constantes
 * `ROW_*_TITLE` acima), já que `jSuites.setDictionary` (B4/UAT-51) traduz `item.title`
 * antes de chegar aqui.
 */
function handleContextMenu(
  instance: SheetWorksheetInstance,
  colIndex: string | number | null,
  rowIndex: string | number | null,
  event: PointerEvent,
  items: ContextMenuItem[],
  role: string
): ContextMenuItem[] {
  if (role !== 'row' && role !== 'cell') return items

  const currentRowCount = instance.rows.length
  const selectedRowCount = instance.getSelectedRows().length || 1
  const insertBlocked = !canInsertRows(currentRowCount, 1, props.maxRows)
  const deleteBlocked = !canDeleteRows(currentRowCount, selectedRowCount, props.minRows)

  return items.map((item) => {
    if (
      insertBlocked &&
      (item.title === ROW_INSERT_BEFORE_TITLE || item.title === ROW_INSERT_AFTER_TITLE)
    ) {
      return { ...item, disabled: true, tooltip: props.rowLimitReason }
    }
    if (deleteBlocked && item.title === ROW_DELETE_TITLE) {
      return { ...item, disabled: true, tooltip: props.minRowLimitReason }
    }
    return item
  })
}

/**
 * RF-08 — conteúdo do toast de resumo da colagem: um parágrafo com `data-testid` próprio,
 * porque o texto agregado (RF-08 (1)-(4)) precisa ser localizável pelo E2E — a
 * `vue-toastification` não expõe `data-testid` nas opções de string simples.
 */
const SheetPasteSummaryToast = defineComponent({
  name: 'SheetPasteSummaryToast',
  props: { message: { type: String, required: true } },
  setup(toastProps) {
    return () => h('p', { 'data-testid': 'sheet-paste-summary' }, toastProps.message)
  }
})

/**
 * RF-08/RN-08/RN-09 (#2116) — a colagem estende o teto de linhas (#2119) com: coerção por
 * tipo de coluna (data/hora/checkbox), descarte agregado do não-parseável e truncamento de
 * largura quando o bloco é mais largo que as colunas disponíveis. A aritmética de linha
 * (`clampPastedRowSpan`) e a coerção por tipo vivem no módulo puro `sheet-paste.ts` — este
 * hook só extrai o bloco cru da lib, delega o cálculo e aplica o resultado.
 *
 * O tipo declarado da lib para o retorno é `undefined | boolean | string`, mas a leitura
 * do código-fonte (`dist/index.js`, função `paste`) mostra que um retorno truthy é
 * atribuído direto à matriz interna de colagem (`s = d`), sem reparse — devolver a matriz
 * calculada aqui funciona em runtime (valores não-string incluídos, ex. `boolean` de
 * checkbox); o cast abaixo é só para a declaração de tipo incompleta da lib, do mesmo
 * jeito que a pesquisa #11 já documentou outra divergência entre o `.d.ts` e o
 * comportamento real do `autoIncrement`.
 *
 * Achado durante #2116, fora do escopo desta issue: o `paste()` nativo pré-processa cada
 * valor colado checando `options.columns[o]` — `o` é o índice RELATIVO ao bloco colado
 * (0, 1, 2...), não o índice ABSOLUTO da coluna de destino (`u`, que soma o offset da
 * célula-alvo). Quando o modelo de folha tem uma coluna `date` numa posição cujo índice
 * relativo colide com uma colagem que começa fora da coluna 0 (ex.: colar um bloco de 2
 * colunas a partir da coluna 2, com a coluna 1 do modelo sendo `date`), a ordem medida em
 * `dist/index.js` (`paste`, jspreadsheet-ce@5.0.4) é a INVERSA da suposição original: a
 * nossa matriz de retorno (RN-08) é atribuída direto à colagem interna da lib (`s = d`)
 * ANTES de a lib rodar `jSuites.calendar.extractDateFromString` — ou seja, a lib
 * reprocessa e corrompe a NOSSA saída, não o contrário. Não há reprodução em
 * `tests/unit/attendance-sheet-card.spec.ts` — a fixture ali evita a colisão de propósito
 * (coluna `text` no índice 0). O bug em si passou a ser dono da web#2379 (split feito pelo
 * dev); não tentar o fix comportamental aqui.
 */
function handleBeforePaste(
  instance: SheetWorksheetInstance,
  copiedText: { value: string | number | boolean }[][],
  colIndex: number | string,
  rowIndex: number | string
): undefined | boolean | string {
  pasteAnchorHistoryIndex = instance.historyIndex

  const block = copiedText.map((row) => row.map((cell) => cell.value))
  const { matrix, summary } = computePasteBlock({
    block,
    columnTypes: props.columns.map((column) => column.type),
    startColIndex: Number(colIndex),
    startRowIndex: Number(rowIndex),
    maxRows: props.maxRows
  })

  if (matrix.length === 0 || matrix.every((row) => row.length === 0)) return false

  const message = buildPasteSummaryMessage(summary, props.maxRows)
  if (message) {
    toast.warning({ component: SheetPasteSummaryToast, props: { message } })
  }

  return matrix as unknown as string
}

/** RF-12 — acrescenta uma linha ao final da tabela; sujeito ao mesmo teto do menu/colagem. */
function addRow() {
  worksheet.value?.insertRow()
}

/**
 * RF-12 — simétrico de `addRow()`: remove a ÚLTIMA linha da tabela, sujeito ao mesmo piso
 * do menu de contexto (`handleBeforeDeleteRow`).
 *
 * É a última linha, e não "a linha selecionada", por uma restrição da lib: qualquer
 * `mousedown` FORA da grade passa por `mouseDownControls`, que chama `resetSelection()` —
 * ao chegar o `click` do botão a seleção já foi apagada, e o destaque dela já sumiu da
 * tela. Um botão que agisse sobre a seleção agiria sobre algo que o usuário não enxerga
 * mais. Exclusão de linha arbitrária/múltipla continua no menu de contexto.
 *
 * O índice vai explícito (em vez de `deleteRow()` sem argumento) justamente para não cair
 * no caminho `getSelectedRows()` da lib, que nesse ponto está vazio e escolheria sozinho.
 */
function deleteRow() {
  const instance = worksheet.value
  if (!instance || !canDeleteRow.value) return
  instance.deleteRow(gridRowCount.value - 1, 1)
}

/**
 * UAT-05/#2321 — Delete numa célula de coluna `date` com default configurado
 * (`newRowDefaults`) devolve esse default em vez de deixar a célula vazia: o Delete
 * nativo do jspreadsheet não passa por nenhum outro hook além deste, escrevendo `''`
 * direto.
 *
 * N7/#2360 — corrigindo a redação original: deste hook ser alcançado pelo Delete **não**
 * segue que só o Delete o alcance. Toda escrita de `''` passa aqui, o RESET do calendário
 * incluído, e a versão anterior anulava as duas por igual. Daí o `emptyWriteFromKeyboard`:
 * a origem vem do `keydown`, porque o valor escrito é idêntico nos dois caminhos.
 *
 * Mesma regra de "limpar volta ao default" já usada em `clearColumn`
 * (column-action.ts) para a ação do menu de coluna — aqui aplicada por célula, no
 * caminho de teclado, que não passa por `computeNewRows`. Colunas fora do tipo `date` ou
 * sem default configurado seguem esvaziando normalmente (`undefined` mantém `newValue`).
 */
function handleBeforeChange(
  instance: SheetWorksheetInstance,
  cell: HTMLTableCellElement,
  colIndex: string | number,
  rowIndex: string | number,
  newValue: string | number | boolean
): string | number | boolean | undefined {
  // UAT-61/RN-07 (#2117) — arrasto da alça em curso: qualquer escrita fora da coluna de
  // origem é o vazamento horizontal (j-03); devolver o próprio valor antigo impede a lib
  // de sequer gravá-la, sem depender de reverter depois (ver `attachFillHandleGuard`).
  if (fillDragColumnRange) {
    const column = Number(colIndex)
    const [minColumn, maxColumn] = fillDragColumnRange
    if (column < minColumn || column > maxColumn) {
      return instance.getValueFromCoords(column, Number(rowIndex)) as string | number | boolean
    }
  }

  if (newValue !== '') return undefined

  // N7/#2360 — só o Delete/Backspace de teclado restaura o default. A redação original deste
  // hook assumia que ele era alcançado SÓ pelo Delete nativo; não é — o RESET do calendário e
  // qualquer escrita programática de `''` passam pelo mesmo caminho e eram anuladas junto.
  // Sem nenhum caminho de UI que esvazie a data, a regra "data em branco em linha em uso é
  // erro" ficava inalcançável (UAT-11 regrediu de passa para falha na rodada 4).
  if (!emptyWriteFromKeyboard) return undefined

  const dateColumn = props.columns[Number(colIndex)]
  if (!dateColumn || dateColumn.type !== 'date') return undefined

  const defaultValue = props.newRowDefaults[dateColumn.key]
  return defaultValue === undefined ? undefined : defaultValue
}

function createGrid() {
  if (props.mode !== 'edit' || props.engine !== 'spreadsheet' || !gridContainer.value) return
  destroyGrid()

  const [instance] = jspreadsheet(gridContainer.value, {
    about: false,
    allowExport: false,
    // RF-09/RNF Customização do drag-fill (#2117) — flag global à instância (não por
    // coluna, research #11): desligada porque o default nativo do CE gera série de +1
    // dia na alça de preenchimento em coluna `calendar`; com ela desligada, toda coluna
    // (incluindo `numeric`, já cópia literal por padrão) replica o valor de origem sem
    // incrementar — a única semântica do RF-09.
    autoIncrement: false,
    worksheets: [
      {
        data: cloneRows(props.rows),
        columns: props.columns.map(toGridColumn),
        minDimensions: [Math.max(props.columns.length, 1), Math.max(props.rows.length, 1)],
        // B4/UAT-51 — não há UI de comentário de célula no escopo desta tela; oculta
        // "Add comments"/"Edit comments"/"Clear comments" do menu de contexto.
        allowComments: false,
        tableOverflow: true,
        tableWidth: '100%',
        search: false,
        filters: false,
        columnSorting: false,
        columnDrag: false,
        columnResize: false,
        allowInsertColumn: false,
        allowDeleteColumn: false,
        allowRenameColumn: false,
        // RN-09: quem dimensiona a folha (linhas) é o auditor — só as colunas são
        // exclusivas do modelo de folha.
        allowInsertRow: true,
        allowDeleteRow: true,
        freezeColumns: 0
      }
    ],
    onafterchanges: (changedInstance, changes) => {
      mergeBatchPasteHistory(changedInstance)
      dropRejectedFillDrag(changedInstance, changes)
      syncFromGrid(changedInstance)
      refreshHistoryState()
    },
    oninsertrow: (changedInstance, insertedRows) => {
      prefillInsertedRows(changedInstance, insertedRows)
      syncFromGrid(changedInstance)
      refreshHistoryState()
    },
    ondeleterow: (changedInstance) => {
      syncFromGrid(changedInstance)
      refreshHistoryState()
    },
    onundo: (changedInstance) => {
      syncFromGrid(changedInstance)
      refreshHistoryState()
    },
    onredo: (changedInstance, historyRecord) => {
      reprefillRedoneRows(changedInstance, historyRecord)
      syncFromGrid(changedInstance)
      refreshHistoryState()
    },
    // RN-09/RF-12/RN-11 (#2119) — teto/piso de linhas nas três vias nativas (botão via
    // `addRow()` exposto, menu de contexto, colagem).
    onbeforeinsertrow: handleBeforeInsertRow,
    onbeforedeleterow: handleBeforeDeleteRow,
    contextMenu: handleContextMenu,
    onbeforepaste: handleBeforePaste,
    onbeforechange: handleBeforeChange
  })

  worksheet.value = instance
  refreshRowCount(instance)
  /*
   * A tela não expõe cabeçalho de número de linha. Isto era feito por CSS nosso, escondendo
   * os `<td>` da primeira coluna — meia correção: com `table-layout: fixed` as células casam
   * com o `<colgroup>` por POSIÇÃO, e célula em `display: none` sai da contagem. O `<col>` de
   * 50px do índice continuava valendo, então cada coluna visível herdava a largura do `<col>`
   * ANTERIOR: a de nome do médico ficava com 50px (o truncamento relatado por UAT) e a
   * seguinte ficava com os 260px destinados ao nome. Medido no Chromium com as larguras de
   * produção: nome 50px / CRM 260px antes, nome 260px / CRM 140px depois.
   *
   * `hideIndex()` é o mecanismo da própria lib: aplica `jss_hidden_index` na tabela, cuja
   * regra de CSS esconde o `<col>` junto com os `<td>` de thead/tbody/tfoot. Os `<td>` seguem
   * no DOM (só invisíveis), então o `td:nth-child(2)` usado no realce de linha parcial
   * continua apontando para a primeira coluna de dados.
   */
  instance.hideIndex()
  patchUndoRedoForPasteGrowth(instance)
  // Fase de captura: sempre roda antes do listener nativo da lib (bubbling, registrado
  // assincronamente) — independe da ordem/tempo de registro em `document`.
  document.addEventListener('keydown', handleShortcutKeyDown, true)
  // UAT-43 (ciclo 1 de review) — marca esta grade como a última interagida a cada
  // mousedown/focusin dentro do próprio container (célula, editor, cabeçalho etc.), para
  // `handleShortcutKeyDown` distinguir esta grade de outra grade ou componente qualquer
  // da página quando o foco depois sai para um alvo "neutro" (ver comentário do handler).
  gridContainer.value.addEventListener('mousedown', handleGridContainerActivity)
  gridContainer.value.addEventListener('focusin', handleGridContainerActivity)
  refreshHistoryState()
  applyRowStyling()
  attachColumnMenus()
  attachFillHandleGuard(instance)
}

/**
 * RF-09/UAT-61 (#2117) — a alça de preenchimento (`instance.corner`) arrasta nativamente em
 * qualquer direção; não há opção nativa para restringi-la à vertical (research #11).
 *
 * A contenção antiga era pós-hoc: revertia o lote de escrita depois de gravado, num
 * `setTimeout(0)` para esperar o listener nativo assíncrono da lib. Isso a deixava frágil a
 * qualquer commit assíncrono concorrente (o datepicker da coluna `date`, por exemplo — j-03)
 * e ao critério "exatamente 1 registro novo de histórico", que quebra na presença de outra
 * escrita concorrente. A contenção atual bloqueia PREVENTIVAMENTE, célula a célula, no mesmo
 * `onbeforechange` do FIX 3 (UAT-05): com um arrasto da alça em curso, qualquer escrita numa
 * coluna fora da seleção de origem devolve o próprio valor antigo da célula — a lib nunca
 * chega a escrever o vazamento, então não há nada para reverter depois.
 *
 * `fillDragColumnRange` guarda a origem, capturada de forma síncrona no mousedown da alça —
 * junto com o fechamento síncrono (`closeEditor(..., true)`) de qualquer editor aberto: sem
 * isso, o commit assíncrono do datepicker corre com a leitura de `instance.edition` durante o
 * próprio arrasto (a race do j-03). A limpeza da origem no fim do arrasto é adiada com
 * `setTimeout(0)` — não para detectar nada (o bloqueio já é síncrono), só para não zerar a
 * origem antes do listener `mouseup` assíncrono da própria lib, que ainda vai gravar o
 * `setValue` do preenchimento (mesma ordem de registro em `document` de sempre: o nosso
 * mousedown/mouseup são síncronos, o da lib só existe depois do factory assíncrono).
 *
 * `dropRejectedFillDrag` cobre RN-07 (nenhum rastro no histórico do arrasto bloqueado):
 * roda no mesmo `onafterchanges` de sempre e, se TODA a escrita do lote caiu fora da origem
 * (bloqueada por inteiro), remove o próprio registro que a lib acabou de empurrar — com
 * valor igual ao antigo em cada célula, o registro nunca teria efeito visível de qualquer
 * forma, então removê-lo é só limpeza do Ctrl+Z, não uma segunda reversão de valor.
 */
let fillDragColumnRange: [number, number] | null = null
let fillDragResetTimeout: ReturnType<typeof setTimeout> | null = null
let detachFillHandleGuard: (() => void) | null = null

function attachFillHandleGuard(instance: SheetWorksheetInstance) {
  function handleCornerMouseDown() {
    if (instance.edition) instance.closeEditor(instance.edition[0], true)
    const [x1, , x2] = instance.getSelection()
    fillDragColumnRange = [Math.min(x1, x2), Math.max(x1, x2)]
  }

  function handleDocumentMouseUp() {
    if (fillDragResetTimeout !== null) clearTimeout(fillDragResetTimeout)
    fillDragResetTimeout = setTimeout(() => {
      fillDragResetTimeout = null
      fillDragColumnRange = null
    }, 0)
  }

  instance.corner.addEventListener('mousedown', handleCornerMouseDown)
  document.addEventListener('mouseup', handleDocumentMouseUp)

  detachFillHandleGuard = () => {
    instance.corner.removeEventListener('mousedown', handleCornerMouseDown)
    document.removeEventListener('mouseup', handleDocumentMouseUp)
    if (fillDragResetTimeout !== null) {
      clearTimeout(fillDragResetTimeout)
      fillDragResetTimeout = null
    }
    fillDragColumnRange = null
  }
}

/** RN-07/UAT-61 — ver comentário de `attachFillHandleGuard` acima. */
function dropRejectedFillDrag(instance: SheetWorksheetInstance, changes: CellChange[]) {
  if (!fillDragColumnRange || changes.length === 0) return

  const [minColumn, maxColumn] = fillDragColumnRange
  const allRejected = changes.every((change) => {
    const column = Number(change.x)
    return column < minColumn || column > maxColumn
  })
  if (!allRejected) return

  instance.history.splice(instance.historyIndex, 1)
  instance.historyIndex -= 1
}

/**
 * RF-01/RF-07 (#2115) — a linha criada pelo auditor (`allowInsertRow`) nasce com a
 * `referenceDate` nas colunas de data, igual à linha inicial do modelo. O preenchimento
 * acontece com `ignoreHistory` ligado para não virar um segundo passo de desfazer: só a
 * criação da linha entra no histórico, nunca o preenchimento da sua célula de data
 * (RF-07). Como o registro de histórico da inserção é guardado pela lib ANTES deste
 * preenchimento rodar, um refazer (`redo`) de uma criação de linha reaplica o registro
 * antigo, sem a data — `reprefillRedoneRows` cobre esse caso reaplicando o mesmo
 * preenchimento no `onredo`.
 */
function prefillInsertedRows(instance: SheetWorksheetInstance, insertedRows: InsertedRow[]) {
  const dateColumnIndexes = props.columns.flatMap((column, index) =>
    column.type === 'date' && props.newRowDefaults[column.key] !== undefined ? [index] : []
  )
  if (dateColumnIndexes.length === 0) return

  const previousIgnoreHistory = instance.ignoreHistory
  instance.ignoreHistory = true
  for (const { row } of insertedRows) {
    for (const colIndex of dateColumnIndexes) {
      const column = props.columns[colIndex]
      instance.setValueFromCoords(colIndex, row, props.newRowDefaults[column.key])
    }
  }
  instance.ignoreHistory = previousIgnoreHistory
}

/**
 * RN-07 (#2115) — quando o `historyRecord` refeito é uma inserção de linha, a lib já
 * recriou a(s) linha(s) com o `rowData` gravado no momento da criação original (antes do
 * prefill de data rodar), então o `redo` as devolve sem a data sugerida. Recalculamos os
 * mesmos índices de linha que a lib usa internamente (`rowNumber`/`numOfRows`/
 * `insertBefore`) e reaplicamos `prefillInsertedRows`, que já usa `ignoreHistory` — o
 * `redo` continua avançando um único passo de histórico.
 */
function reprefillRedoneRows(instance: SheetWorksheetInstance, historyRecord: unknown) {
  const record = historyRecord as Partial<InsertRowHistoryRecord> | undefined
  if (
    !record ||
    record.action !== 'insertRow' ||
    typeof record.rowNumber !== 'number' ||
    typeof record.numOfRows !== 'number'
  ) {
    return
  }

  const firstRow = record.rowNumber + (record.insertBefore ? 0 : 1)
  const insertedRows: InsertedRow[] = Array.from({ length: record.numOfRows }, (_, offset) => ({
    row: firstRow + offset,
    data: []
  }))
  prefillInsertedRows(instance, insertedRows)
}

/**
 * `getData()` devolve a matriz crua indexada por posição de coluna, não pelo `name`
 * declarado — remapeada aqui para as chaves que a tela espera (`SheetColumn.key`), tanto
 * para o sync reativo (`emit`) quanto para a leitura sob demanda do menu de coluna.
 *
 * O 2º parâmetro (`processed`) é `false` de propósito (B1, PRD 32 run 3): `true` devolve o
 * `innerHTML` de cada célula (data já formatada `DD/MM/YYYY`, checkbox sempre o markup
 * `<input type="checkbox">`), não o dado — isso fazia a RN-03 tratar toda sugestão de data
 * como conteúdo transcrito e todo checkbox como marcado. `false` devolve o valor cru de
 * `options.data`, que é o que a classificação e o payload ao backend esperam. Colunas
 * `date` ainda passam por `toCanonicalDateOnly` porque o dado cru chega em 3 formas
 * possíveis (ISO, ISO com timestamp do date picker, ou `DD/MM/YYYY` de digitação/colagem).
 */
function readRowsFromInstance(instance: SheetWorksheetInstance): EditableSheetRow[] {
  const matrix = instance.getData() as unknown as (string | boolean)[][]
  return matrix.map((row) => {
    const mapped: EditableSheetRow = {}
    props.columns.forEach((col, colIndex) => {
      const value = row[colIndex]
      mapped[col.key] = col.type === 'date' ? toCanonicalDateOnly(value) : value
    })
    return mapped
  })
}

function syncFromGrid(instance: SheetWorksheetInstance) {
  // UAT-43 (ciclo 1 de review) — toda mudança processada por esta grade é prova de que
  // ela é a interagida no momento; cobre tanto a interação real do usuário (que já marca
  // via mousedown/focusin no container, ver `createGrid`) quanto uma chamada
  // programática direto na instância (ex.: `setValueFromCoords` nos testes), que não
  // passa por nenhum evento DOM do container.
  if (gridContainer.value) markSheetGridActive(gridContainer.value)
  refreshRowCount(instance)
  emit('update:rows', readRowsFromInstance(instance))
  applyRowStyling()
}

/**
 * RF-02 — a faixa de linha parcial (3px) e o erro de validação na célula vêm de
 * `rowErrors`, recalculado pela tela a cada edição. O jspreadsheet não tem essas duas
 * marcações declarativamente, então elas são aplicadas direto no DOM que ele gera.
 */
function applyRowStyling() {
  const instance = worksheet.value
  if (!instance) return

  // Itera o tamanho da própria instância, não `props.rows`: em `ondeleterow`, a prop
  // ainda reflete o tamanho anterior à exclusão (o re-render do pai que a atualiza é
  // assíncrono), e `instance.getCell` não devolve `null` para uma linha fora do range —
  // ele lança (lê `this.records[y][x].element` sem checar limites). `try/catch` por linha
  // é o cinto de segurança para esse (e qualquer outro) descompasso momentâneo.
  const rowCount = (instance.options.data as unknown[][] | undefined)?.length ?? 0

  for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
    try {
      const rowCell = instance.getCell(0, rowIndex) as HTMLTableCellElement | null
      rowCell?.parentElement?.classList.toggle(
        'sheet-row-partial',
        props.rowErrors[rowIndex] !== undefined
      )

      props.columns.forEach((col, colIndex) => {
        const cell = instance.getCell(colIndex, rowIndex) as HTMLTableCellElement | null
        if (!cell) return
        const invalid = props.rowErrors[rowIndex]?.includes(col.key) ?? false
        cell.classList.toggle('sheet-cell-invalid', invalid)
        cell.setAttribute(DATA_TESTID_ATTR, `sheet-cell-${col.key}-${rowIndex}`)
        cell.title = invalid ? CELL_ERROR_MESSAGE : ''
      })
    } catch {
      // Linha momentaneamente fora de sincronia com a instância — pula, o próximo
      // `applyRowStyling` (disparado pelo watcher de `rows`/`rowErrors`) resolve.
      continue
    }
  }
}

/**
 * RF-06 (#2113) — menu `▾` do cabeçalho, um por coluna elegível. O botão fica dentro do
 * container do grid (escopo do CSS do componente); o popover vai para `document.body`
 * (mesma razão do editor de sugestão de médico: escapar do `overflow-x-auto` da tabela).
 */
let closeActiveColumnMenu: (() => void) | null = null

function closeColumnMenu() {
  closeActiveColumnMenu?.()
  closeActiveColumnMenu = null
}

function attachColumnMenus() {
  const instance = worksheet.value
  if (!instance || !props.getColumnMenuItem) return

  props.columns.forEach((col, colIndex) => {
    const header = instance.headers?.[colIndex] as HTMLTableCellElement | undefined
    if (!header || !props.getColumnMenuItem!(col.key)) return

    const label = header.textContent ?? col.label
    header.innerHTML = ''

    const inner = document.createElement('div')
    inner.className = 'sheet-column-header-inner'

    const labelSpan = document.createElement('span')
    labelSpan.className = 'sheet-column-header-label'
    labelSpan.textContent = label
    inner.appendChild(labelSpan)

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'sheet-column-menu-button'
    button.setAttribute(DATA_TESTID_ATTR, `sheet-column-menu-button-${col.key}`)
    button.setAttribute('aria-haspopup', 'menu')
    button.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18"><path d="${mdiMenuDown}" fill="currentColor" /></svg>`
    button.addEventListener('click', (event) => {
      event.stopPropagation()
      openColumnMenu(col, colIndex, button)
    })
    inner.appendChild(button)

    header.appendChild(inner)
  })
}

/** RN-11 — motivo à vista no próprio menu; RF-06 — hint do que a ação vai fazer. */
function openColumnMenu(col: SheetColumn, colIndex: number, anchor: HTMLButtonElement) {
  closeColumnMenu()

  const item = props.getColumnMenuItem?.(col.key)
  if (!item) return

  const popover = document.createElement('div')
  popover.className = 'sheet-column-menu-popover'
  popover.setAttribute('role', 'menu')
  popover.setAttribute(DATA_TESTID_ATTR, `sheet-column-menu-${col.key}`)

  const menuItem = document.createElement('button')
  menuItem.type = 'button'
  menuItem.className = 'sheet-column-menu-item'
  menuItem.setAttribute('role', 'menuitem')
  menuItem.setAttribute(DATA_TESTID_ATTR, `sheet-column-menu-item-${col.key}`)
  menuItem.textContent = item.label
  menuItem.disabled = item.disabled
  if (item.disabled) menuItem.setAttribute('aria-disabled', 'true')
  popover.appendChild(menuItem)

  const supportText = item.disabled ? item.reason : item.hint
  if (supportText) {
    menuItem.title = item.disabled ? supportText : ''
    const supportEl = document.createElement('p')
    supportEl.className = item.disabled ? 'sheet-column-menu-reason' : 'sheet-column-menu-hint'
    supportEl.setAttribute(
      DATA_TESTID_ATTR,
      item.disabled ? `sheet-column-menu-reason-${col.key}` : `sheet-column-menu-hint-${col.key}`
    )
    supportEl.textContent = supportText
    popover.appendChild(supportEl)
  }

  if (!item.disabled) {
    menuItem.addEventListener('click', () => {
      const instance = worksheet.value
      if (!instance) return

      const currentRows = readRowsFromInstance(instance)
      const newRows = item.computeNewRows(currentRows)
      const records: { x: number; y: number; value: string | number | boolean }[] = []
      newRows.forEach((row, y) => {
        const value = row[col.key]
        if (value !== currentRows[y]?.[col.key]) {
          records.push({ x: colIndex, y, value: value as string | number | boolean })
        }
      })
      // RN-07 — toda a ação (aplicar, limpar ou marcar em massa) é um único lote de
      // escrita, gravado pelo jspreadsheet como um único passo de desfazer.
      if (records.length > 0) instance.setValue(records)
      closeColumnMenu()
    })
  }

  document.body.appendChild(popover)
  const rect = anchor.getBoundingClientRect()
  popover.style.top = `${rect.bottom + 4}px`
  popover.style.left = `${rect.left}px`
  anchor.setAttribute('aria-expanded', 'true')

  function handleOutsideClick(event: MouseEvent) {
    const target = event.target as Node
    if (popover.contains(target) || anchor.contains(target)) return
    closeColumnMenu()
  }
  document.addEventListener('mousedown', handleOutsideClick)

  closeActiveColumnMenu = () => {
    document.removeEventListener('mousedown', handleOutsideClick)
    popover.remove()
    anchor.removeAttribute('aria-expanded')
  }
}

onMounted(createGrid)
onBeforeUnmount(() => {
  closeColumnMenu()
  destroyGrid()
})

watch(
  () => props.mode,
  (mode) => {
    if (mode === 'edit' && props.engine === 'spreadsheet') createGrid()
    else destroyGrid()
  }
)

watch(
  () => props.engine,
  (engine) => {
    if (props.mode === 'edit' && engine === 'spreadsheet') createGrid()
    else destroyGrid()
  }
)

watch(
  () => props.columns,
  () => createGrid()
)

/**
 * B1 (PRD 32 run 3) — a mesma dupla `getData()` cru + `toCanonicalDateOnly` nas colunas
 * `date` de `readRowsFromInstance`, para os dois lados da comparação ficarem na mesma
 * normalização. Comparar dado cru contra `props.rows` (já canônico) fazia esta comparação
 * divergir a cada edição e chamar `setData` sem necessidade — o que apaga `history`/
 * `historyIndex` da lib (regressão de undo/redo, RF-07).
 */
function toComparableMatrix(rows: SheetRow[]): unknown[][] {
  return rows.map((row) =>
    props.columns.map((col) => {
      const value = row[col.key] ?? ''
      return col.type === 'date' ? toCanonicalDateOnly(value) : value
    })
  )
}

watch(
  () => props.rows,
  (newRows) => {
    if (props.mode !== 'edit' || !worksheet.value) return
    const currentMatrix = toComparableMatrix(readRowsFromInstance(worksheet.value))
    const newMatrix = toComparableMatrix(newRows)
    if (JSON.stringify(currentMatrix) !== JSON.stringify(newMatrix)) {
      worksheet.value.setData(cloneRows(newRows))
      // `setData` reescreve a matriz sem passar por `syncFromGrid` — é a única outra via
      // que muda a quantidade de linhas da grade.
      refreshRowCount(worksheet.value)
    }
    applyRowStyling()
  },
  { deep: true }
)

watch(() => props.rowErrors, applyRowStyling, { deep: true })

return (_ctx: any,_cache: any) => {
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.mode === 'view')
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("table", _hoisted_3, [
            _createElementVNode("thead", null, [
              _createElementVNode("tr", _hoisted_4, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (col) => {
                  return (_openBlock(), _createElementBlock("th", {
                    key: col.key,
                    class: "font-bold text-center whitespace-nowrap px-3 pb-3",
                    style: _normalizeStyle({ width: _unref(toCssWidth)(col) })
                  }, [
                    _createElementVNode("div", _hoisted_5, [
                      (col.tooltip)
                        ? (_openBlock(), _createBlock(_component_v_tooltip, {
                            key: 0,
                            location: "top"
                          }, {
                            activator: _withCtx(({ props: tooltipProps }) => [
                              _createElementVNode("div", _mergeProps(tooltipProps, { class: "flex items-center justify-center gap-1.5 cursor-help transition-opacity hover:opacity-80" }), [
                                (_openBlock(), _createElementBlock("svg", _hoisted_6, [
                                  _createElementVNode("path", {
                                    d: _unref(mdiInformationOutline),
                                    fill: "currentColor"
                                  }, null, 8, _hoisted_7)
                                ])),
                                _createTextVNode(" " + _toDisplayString(col.label), 1)
                              ], 16)
                            ]),
                            default: _withCtx(() => [
                              _createElementVNode("span", {
                                innerHTML: col.tooltip
                              }, null, 8, _hoisted_8)
                            ]),
                            _: 2
                          }, 1024))
                        : (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(col.label), 1))
                    ])
                  ], 4))
                }), 128))
              ])
            ]),
            _createElementVNode("tbody", _hoisted_10, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.rows, (row, rowIndex) => {
                return (_openBlock(), _createElementBlock("tr", {
                  key: rowIndex,
                  class: "text-[#4B5563] text-sm transition-colors"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (col) => {
                    return (_openBlock(), _createElementBlock("td", {
                      key: col.key,
                      class: "px-2 py-1.5 align-middle text-center whitespace-nowrap"
                    }, [
                      _renderSlot(_ctx.$slots, `cell-view-${col.key}`, {
                        row: row,
                        col: col,
                        value: row[col.key]
                      }, () => [
                        (col.type === 'status')
                          ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                              (asStatus(row[col.key])?.type === 'success')
                                ? (_openBlock(), _createElementBlock("svg", _hoisted_12, [
                                    _createElementVNode("path", {
                                      d: _unref(mdiCheckCircleOutline),
                                      fill: "currentColor"
                                    }, null, 8, _hoisted_13)
                                  ]))
                                : (asStatus(row[col.key])?.type === 'warning')
                                  ? (_openBlock(), _createElementBlock("svg", _hoisted_14, [
                                      _createElementVNode("path", {
                                        d: _unref(mdiAlertCircleOutline),
                                        fill: "currentColor"
                                      }, null, 8, _hoisted_15)
                                    ]))
                                  : _createCommentVNode("", true),
                              _createElementVNode("span", {
                                class: _normalizeClass(
                      asStatus(row[col.key])?.type === 'warning' ? 'text-gray-600' : 'text-gray-500'
                    )
                              }, _toDisplayString(asStatus(row[col.key])?.text), 3)
                            ]))
                          : (col.type === 'checkbox')
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 1,
                                class: _normalizeClass(["flex justify-center items-center h-full", col.cellClass])
                              }, [
                                _createVNode(_component_v_checkbox, {
                                  "model-value": row[col.key],
                                  density: "compact",
                                  "hide-details": "",
                                  readonly: "",
                                  color: "#9CA3AF",
                                  class: "flex-none m-0 p-0 custom-checkbox-view"
                                }, null, 8, ["model-value"])
                              ], 2))
                            : (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(row[col.key]), 1))
                      ])
                    ]))
                  }), 128))
                ]))
              }), 128))
            ])
          ])
        ]))
      : (__props.engine === 'native')
        ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
            _createElementVNode("table", _hoisted_18, [
              _createElementVNode("thead", null, [
                _createElementVNode("tr", _hoisted_19, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (col) => {
                    return (_openBlock(), _createElementBlock("th", {
                      key: col.key,
                      class: "font-bold text-center whitespace-nowrap px-3 pb-3",
                      style: _normalizeStyle({ width: _unref(toCssWidth)(col) })
                    }, [
                      _createElementVNode("div", _hoisted_20, [
                        (col.tooltip)
                          ? (_openBlock(), _createBlock(_component_v_tooltip, {
                              key: 0,
                              location: "top"
                            }, {
                              activator: _withCtx(({ props: tooltipProps }) => [
                                _createElementVNode("div", _mergeProps(tooltipProps, { class: "flex items-center justify-center gap-1.5 cursor-help transition-opacity hover:opacity-80" }), [
                                  (_openBlock(), _createElementBlock("svg", _hoisted_21, [
                                    _createElementVNode("path", {
                                      d: _unref(mdiInformationOutline),
                                      fill: "currentColor"
                                    }, null, 8, _hoisted_22)
                                  ])),
                                  _createTextVNode(" " + _toDisplayString(col.label), 1)
                                ], 16)
                              ]),
                              default: _withCtx(() => [
                                _createElementVNode("span", {
                                  innerHTML: col.tooltip
                                }, null, 8, _hoisted_23)
                              ]),
                              _: 2
                            }, 1024))
                          : (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(col.label), 1))
                      ])
                    ], 4))
                  }), 128))
                ])
              ]),
              _createElementVNode("tbody", _hoisted_25, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(localRows.value, (row, rowIndex) => {
                  return (_openBlock(), _createElementBlock("tr", {
                    key: rowIndex,
                    class: "text-[#4B5563] text-sm transition-colors"
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.columns, (col, colIndex) => {
                      return (_openBlock(), _createElementBlock("td", {
                        key: col.key,
                        class: _normalizeClass(["px-2 py-1.5 align-middle text-center whitespace-nowrap", 
                colIndex === 0 && isPartialRow(rowIndex) ? 'border-l-[3px] border-warning' : ''
              ])
                      }, [
                        _renderSlot(_ctx.$slots, `cell-edit-${col.key}`, {
                          row: row,
                          col: col,
                          value: row[col.key],
                          rowIndex: rowIndex
                        }, () => [
                          (['text', 'date', 'time', 'number'].includes(col.type))
                            ? (_openBlock(), _createElementBlock("input", {
                                key: 0,
                                type: col.type,
                                value: (row[col.key] as string | number | undefined) ?? '',
                                "data-testid": `sheet-cell-${col.key}-${rowIndex}`,
                                "aria-invalid": isCellInvalid(rowIndex, col.key),
                                title: isCellInvalid(rowIndex, col.key) ? CELL_ERROR_MESSAGE : undefined,
                                class: _normalizeClass(["bg-white border rounded-md px-3 py-2 w-full text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#2F7F91] focus:border-transparent transition-all", 
                    isCellInvalid(rowIndex, col.key)
                      ? 'border-negative ring-1 ring-negative'
                      : 'border-gray-400'
                  ]),
                                onInput: ($event: any) => (
                    updateEditCell(rowIndex, col.key, ($event.target as HTMLInputElement).value)
                  )
                              }, null, 42, _hoisted_26))
                            : (col.type === 'checkbox')
                              ? (_openBlock(), _createElementBlock("div", {
                                  key: 1,
                                  class: _normalizeClass(["flex justify-center items-center rounded-md h-[38px] bg-white border", [
                    col.cellClass,
                    isCellInvalid(rowIndex, col.key) ? 'border-negative' : 'border-transparent'
                  ]]),
                                  title: isCellInvalid(rowIndex, col.key) ? CELL_ERROR_MESSAGE : undefined
                                }, [
                                  _createVNode(_component_v_checkbox, {
                                    "model-value": Boolean(row[col.key]),
                                    density: "compact",
                                    "hide-details": "",
                                    color: "#2F7F91",
                                    class: "flex-none m-0 p-0",
                                    "data-testid": `sheet-cell-${col.key}-${rowIndex}`,
                                    "onUpdate:modelValue": 
                      (value) => updateEditCell(rowIndex, col.key, Boolean(value))
                    
                                  }, null, 8, ["model-value", "data-testid", "onUpdate:modelValue"])
                                ], 10, _hoisted_27))
                              : (_openBlock(), _createElementBlock("span", _hoisted_28, _toDisplayString(row[col.key]), 1))
                        ])
                      ], 2))
                    }), 128))
                  ]))
                }), 128))
              ])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_29, [
            _createElementVNode("div", {
              ref_key: "gridContainer",
              ref: gridContainer
            }, null, 512)
          ]))
  ]))
}
}

})